import clamp from 'clamp';
import clsx from 'clsx';
import * as React from 'react';

import { usePanelTransitionStyle } from '@gitbook/spine-react';

import {
    AGENT_RAIL_MAX_WIDTH,
    AGENT_RAIL_MIN_WIDTH,
    AgentDockSlot,
    useAgentRailWidth,
} from '../Components';

const KEYBOARD_RESIZE_STEP = 16;

/**
 * The column the layout keeps on the right of the content.
 *
 * It holds two things, and the split between them is the whole point of it: the page's side panel
 * at the top — rendered here rather than over the page, through the host below — and the anchored
 * agent under it. The agent has the column to itself until a page opens a panel, and takes it back
 * when that panel closes, so moving between pages never costs the conversation its place.
 *
 * Which of the two grows is decided in CSS, from whether the host is holding anything: nothing here
 * watches the page's side panels, and nothing has to be told when one opens.
 */
export function AppLayoutRail(props: {
    /**
     * Receives the element the page's side panel is rendered into. Handed to
     * `SidePanelHostContext` by the layout, which owns it: the panel has to be pointed at the host
     * during the same render the rail appears in, or it would mount over the page and move here a
     * frame later — remounting itself on the way.
     */
    hostRef: React.Ref<HTMLDivElement>;
}) {
    const { hostRef } = props;
    const railRef = React.useRef<HTMLDivElement>(null);
    const [storedWidth, setStoredWidth] = useAgentRailWidth();
    const [draggedWidth, setDraggedWidth] = React.useState<number | null>(null);
    const width = draggedWidth ?? storedWidth;
    // The width follows the side panel's own transition, so expanding a panel widens the column at
    // the pace the panel itself changes size. Not while dragging, where it has to follow the pointer.
    const transitionStyle = usePanelTransitionStyle();

    return (
        <div
            ref={railRef}
            data-testid="appLayout.rail"
            // The gutter is added to the width rather than taken out of it, so the width the user
            // picks is the cards'. Never more than half the window, which a narrower window enforces
            // without rewriting what the user picked. A page's expanded panel widens it to at least
            // its own expanded width.
            className="relative flex w-[calc(var(--app-rail-width)+var(--app-layout-spacing-unit))] shrink-0 flex-col gap-1.5 py-1.5 pr-1.5 has-[[data-side-panel-expanded]]:w-[calc(max(var(--app-rail-width),var(--app-rail-width-expanded))+var(--app-layout-spacing-unit))]"
            style={
                {
                    ...transitionStyle,
                    transitionProperty: draggedWidth === null ? 'width' : 'none',
                    '--app-rail-width': `min(${width}px, 50vw)`,
                } as React.CSSProperties
            }
        >
            <RailResizeHandle
                width={width}
                railRef={railRef}
                onResize={setDraggedWidth}
                onResizeEnd={(nextWidth) => {
                    setDraggedWidth(null);
                    setStoredWidth(nextWidth);
                }}
            />
            {/* Where the page's side panel lands. Empty for as long as the page has none open,
                which is also how the agent below comes to have the column to itself. */}
            <div
                ref={hostRef}
                data-app-rail-host
                className="flex min-h-64 flex-1 flex-col overflow-hidden empty:hidden"
            />
            {/* Both grow the same, so the column is halved when they share it and whole when only
                one of them is there. Neither goes under the floor, which on a short window takes
                the difference out of both rather than crushing one of them. */}
            <AgentDockSlot kind="rail" className="flex min-h-64 flex-1 flex-col" />
        </div>
    );
}

/**
 * The rail's left edge, dragged to resize it. Measured from the rail's right edge, which stays put.
 */
function RailResizeHandle(props: {
    width: number;
    railRef: React.RefObject<HTMLElement | null>;
    onResize: (width: number) => void;
    onResizeEnd: (width: number) => void;
}) {
    const { width, railRef, onResize, onResizeEnd } = props;
    const handleRef = React.useRef<HTMLDivElement>(null);
    const [isDragging, setDragging] = React.useState(false);

    const widthFromPointer = (clientX: number) => {
        const rail = railRef.current;
        if (!rail) {
            return width;
        }
        // The gutter on the rail's right is not part of the width the cards get.
        const gutter = parseFloat(getComputedStyle(rail).paddingRight);
        return clamp(
            Math.round(rail.getBoundingClientRect().right - gutter - clientX),
            AGENT_RAIL_MIN_WIDTH,
            getRailMaxWidth()
        );
    };

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
            return;
        }
        event.preventDefault();
        handleRef.current?.setPointerCapture(event.pointerId);
        setDragging(true);
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (isDragging) {
            onResize(widthFromPointer(event.clientX));
        }
    };

    const endDrag = (event: React.PointerEvent<HTMLDivElement>, nextWidth: number) => {
        if (!isDragging) {
            return;
        }
        handleRef.current?.releasePointerCapture(event.pointerId);
        setDragging(false);
        onResizeEnd(nextWidth);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        // The handle is on the rail's left edge: moving it left widens the rail.
        const step =
            event.key === 'ArrowLeft'
                ? KEYBOARD_RESIZE_STEP
                : event.key === 'ArrowRight'
                  ? -KEYBOARD_RESIZE_STEP
                  : 0;
        if (step === 0) {
            return;
        }
        event.preventDefault();
        onResizeEnd(clamp(width + step, AGENT_RAIL_MIN_WIDTH, getRailMaxWidth()));
    };

    React.useEffect(() => {
        if (!isDragging) {
            return;
        }
        document.body.classList.add('resizing-sidebar');
        return () => {
            document.body.classList.remove('resizing-sidebar');
        };
    }, [isDragging]);

    return (
        <div
            ref={handleRef}
            tabIndex={0}
            role="separator"
            aria-label="Resize side panel"
            aria-orientation="vertical"
            aria-valuemin={AGENT_RAIL_MIN_WIDTH}
            aria-valuemax={AGENT_RAIL_MAX_WIDTH}
            aria-valuenow={Math.round(width)}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={(event) => endDrag(event, widthFromPointer(event.clientX))}
            onPointerCancel={(event) => endDrag(event, width)}
            onKeyDown={handleKeyDown}
            // Fills the gutter the content's card keeps on its right, so the line sits halfway between
            // the two cards and covers neither.
            className="group absolute inset-y-4 -left-1.5 z-10 flex w-1.5 cursor-col-resize justify-center focus-visible:outline-hidden"
        >
            <div
                className={clsx(
                    'w-[2px] rounded-sm transition group-hover:bg-neutral-solid group-focus-visible:bg-neutral-solid',
                    isDragging && 'bg-neutral-solid'
                )}
            />
        </div>
    );
}

/** The rail is never more than half the window, as its stylesheet caps it. */
function getRailMaxWidth() {
    return Math.min(AGENT_RAIL_MAX_WIDTH, window.innerWidth / 2);
}
